import {
  HYDRATED_QUERY_OPTIONS,
  hotStoreListQuery,
  storeListQuery,
} from '@/lib/page-queries';
import {
  buildRecommendedStoreParams,
  pickRecommendedStore,
} from '@/lib/recommended-store';
import { useQuery } from '@tanstack/react-query';
import { ApiResponse, QUERY_KEYS } from '../base-api';
import {
  getKeywordList,
  getStoreContactList,
  getStoreDetail,
  getStoreList,
  getStoreNameList,
} from '../store.service';
import {
  GetStoreListParams,
  KeywordEntity,
  StoreContactEntity,
  StoreEntity,
  StoreListResponse,
} from '../types/store.type';

export const useStoreContactList = (type: string) => {
  return useQuery<ApiResponse<StoreContactEntity[]>>({
    queryKey: [QUERY_KEYS.STORE, 'contacts', type],
    queryFn: () => getStoreContactList(type),
  });
};

export const useStoreList = (
  params?: GetStoreListParams,
  initialData?: ApiResponse<StoreListResponse>,
) => {
  return useQuery<ApiResponse<StoreListResponse>>({
    ...storeListQuery(params),
    ...HYDRATED_QUERY_OPTIONS,
    // 초기 로드(지역·1페이지·필터 없음)에 한해 서버 목록을 초기값으로 주입해
    // 카드 목록을 SSR HTML에 노출한다(GEO/AEO). 호출부에서 게이트로 제어.
    initialData,
  });
};

/**
 * 학원 목록 상단 추천 실내운전연습장 1곳(규칙은 lib/recommended-store).
 * 반경 내 매장이 없으면 null. 지역이 바뀌는 동안 이전 결과를 유지해 배너 깜빡임을 줄인다.
 */
export const useRecommendedStore = (
  region?: string,
  initialData?: StoreEntity | null,
) => {
  return useQuery<StoreEntity | null>({
    queryKey: [QUERY_KEYS.STORE, 'recommended', region],
    queryFn: async () => {
      const response = await getStoreList(
        buildRecommendedStoreParams(region as string),
      );
      return pickRecommendedStore(response.data?.stores);
    },
    enabled: !!region,
    // 서버에서 고른 추천 매장을 초기값으로 넣어 SSR HTML에 노출한다(검색 결과 카드 순서).
    initialData,
    placeholderData: (previous) => previous,
  });
};

export const useHotStoreList = () => {
  return useQuery<ApiResponse<StoreEntity[]>>({
    ...hotStoreListQuery(),
    ...HYDRATED_QUERY_OPTIONS,
  });
};

export const useStoreDetail = (
  id: number,
  initialData?: ApiResponse<StoreEntity>,
) => {
  return useQuery<ApiResponse<StoreEntity>>({
    queryKey: [QUERY_KEYS.STORE, QUERY_KEYS.DETAIL, id],
    queryFn: () => getStoreDetail(id),
    enabled: !!id,
    // 서버에서 미리 받은 상세를 초기값으로 넣어 본문을 SSR HTML에 노출(GEO/AEO).
    initialData,
  });
};

export const useKeywordList = () => {
  return useQuery<ApiResponse<KeywordEntity[]>>({
    queryKey: [QUERY_KEYS.STORE, 'keywords'],
    queryFn: getKeywordList,
  });
};

export const useStoreNameList = () => {
  return useQuery<ApiResponse<StoreEntity[]>>({
    queryKey: [QUERY_KEYS.STORE, 'name-list'],
    queryFn: getStoreNameList,
  });
};
